/* TickerLink — clickable + right-clickable ticker label used in Scanner and
   Market Watch. Single click → onAnalyse(ticker). Right-click opens a small
   context menu with: Analyse, Add to Vault, Add to On Deck, Log a trade.
   Hover shows a 🔍 icon and tooltip "Analyse {ticker} →". */
import { useEffect, useRef, useState } from "react";
import { C } from "./_shared";

const API = process.env.REACT_APP_BACKEND_URL || "";

async function apiPost(path, body) {
  try {
    const res = await fetch(`${API}${path}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    return await res.json();
  } catch (e) {
    return { ok: false, error: e?.message || "network" };
  }
}

export default function TickerLink({ ticker, name, onAnalyse, onLogTrade, size = 13 }) {
  const [hover, setHover] = useState(false);
  const [menu, setMenu]   = useState(null); // { x, y } | null
  const [toast, setToast] = useState("");
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(null);
    document.addEventListener("click", close);
    document.addEventListener("scroll", close, true);
    return () => {
      document.removeEventListener("click", close);
      document.removeEventListener("scroll", close, true);
    };
  }, [menu]);

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(""), 2500);
  }

  async function addToVault() {
    setMenu(null);
    const res = await apiPost("/api/trading/vault", {
      ticker, name: name || ticker, sector: "", conviction: 3, avgVol: "—", notes: "Added from quick action",
    });
    showToast(res?.id ? `🔒 ${ticker} added to Vault` : `Failed to add ${ticker}`);
  }

  async function addToOnDeck() {
    setMenu(null);
    const res = await apiPost("/api/trading/ondeck", {
      ticker, name: name || ticker, sector: "", stage: "EMA Tightening", readiness: 50, catalysts: [], why: "Added from quick action",
    });
    showToast(res?.id ? `⚡ ${ticker} added to On Deck` : `Failed to add ${ticker}`);
  }

  function logTrade() {
    setMenu(null);
    if (typeof onLogTrade === "function") onLogTrade(ticker);
  }

  function analyse() {
    setMenu(null);
    if (typeof onAnalyse === "function") onAnalyse(ticker);
  }

  const items = [
    { label: "🔍  Analyse this stock",    onClick: analyse },
    { label: "🔒  Add to Vault",          onClick: addToVault },
    { label: "⚡  Add to On Deck",        onClick: addToOnDeck },
    { label: "📋  Log a trade for this stock", onClick: logTrade },
  ];

  return (
    <span
      ref={wrapRef}
      data-testid={`ticker-link-${ticker}`}
      onClick={analyse}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onContextMenu={(e) => {
        e.preventDefault();
        // Position the menu, clamping to viewport.
        const x = Math.min(e.clientX, window.innerWidth - 220);
        const y = Math.min(e.clientY, window.innerHeight - 180);
        setMenu({ x, y });
      }}
      title={name && name !== ticker ? `Analyse ${ticker} — ${name} →` : `Analyse ${ticker} →`}
      style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        cursor: "pointer", color: hover ? C.indigo : "#1e40af",
        fontWeight: 800, fontSize: size,
        textDecoration: hover ? "underline" : "none",
        userSelect: "none",
      }}
    >
      {ticker}
      {hover && <span style={{ fontSize: size - 3, opacity: 0.7 }}>🔍</span>}

      {menu && (
        <div
          data-testid={`ticker-menu-${ticker}`}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "fixed", left: menu.x, top: menu.y, zIndex: 9999,
            background: "#fff", border: "1px solid #d1d5db", borderRadius: 8,
            boxShadow: "0 10px 30px rgba(15,23,42,0.18)",
            minWidth: 220, padding: 4, fontSize: 12, color: "#111",
            fontWeight: 500,
          }}
        >
          <div style={{ padding: "6px 10px", fontSize: 10, color: C.gray, textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700, borderBottom: "0.5px solid #f3f4f6", marginBottom: 2 }}>{ticker}</div>
          {items.map((it, i) => (
            <button
              key={i}
              onClick={(e) => { e.stopPropagation(); it.onClick(); }}
              data-testid={`ticker-menu-${ticker}-${i}`}
              style={{
                display: "block", width: "100%", textAlign: "left",
                padding: "8px 12px", border: "none", background: "transparent",
                cursor: "pointer", borderRadius: 5, fontSize: 12, color: "#111",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#f1f5f9")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              {it.label}
            </button>
          ))}
        </div>
      )}

      {toast && (
        <span
          data-testid={`ticker-toast-${ticker}`}
          style={{
            position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
            background: "#0f172a", color: "#fff", padding: "8px 16px",
            borderRadius: 8, fontSize: 12, fontWeight: 600, zIndex: 9999,
            boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
          }}
        >
          {toast}
        </span>
      )}
    </span>
  );
}
